import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, FileText, AlertTriangle, Truck, Calculator, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1766608422198-5be9ac0aac9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx0cnVja2luZyUyMGxvZ2lzdGljcyUyMGhpZ2h3YXklMjBzZW1pJTIwdHJ1Y2t8ZW58MHx8fHwxNzc3NDkzMjY2fDA&ixlib=rb-4.1.0&q=85";

const VALUE_PROPS = [
  { icon: FileText, title: "Every filing, tracked",
    text: "IRP · IFTA Q1-Q4 · UCR · KYU · MCS-150 · BOC-3 · HVUT-2290 · DOT Annual — one dashboard." },
  { icon: AlertTriangle, title: "60-day expiry alerts",
    text: "CDL, medical card, drug test, insurance, DOT inspection — we ping you before anything lapses." },
  { icon: Truck, title: "Trucks & drivers, one tile each",
    text: "VIN, plates, IRP, insurance, inspection history. Two drivers per truck for team operations." },
  { icon: ShieldCheck, title: "Live FMCSA monitoring",
    text: "Pull your official Carrier Snapshot, OOS rates, crashes and safety rating — straight from the source." },
  { icon: Calculator, title: "IFTA calculator built-in",
    text: "Type miles + fuel by state, get tax owed/credit. No spreadsheet, no surprise notices." },
  { icon: BarChart3, title: "Safety score you can defend",
    text: "Internal compliance score from logged inspections, citations, drug tests and driver risk." },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email.trim(), pwd);
      toast.success("Welcome back");
      nav(u.role === "super_admin" ? "/super-admin" : u.role === "admin" ? "/admin" : "/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-slate-900">
      <div className="relative hidden lg:block bg-blue-900">
        <img src={HERO} alt="truck" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-blue-800/90 to-blue-700/80" />
        <div className="relative z-10 h-full flex flex-col p-10 text-white overflow-y-auto">
          <div className="bg-white px-5 py-4 inline-flex self-start shadow-lg mb-8">
            <img src="/logo.png" alt="Crystal Logistics Services" className="h-20 w-auto" />
          </div>
          <div className="hs-stripe" />
          <h1 className="font-display font-black text-4xl uppercase leading-[0.95] tracking-tighter mt-4">
            Safety &<br />Compliance,<br />
            <span className="text-blue-200">Managed.</span>
          </h1>
          <p className="mt-4 text-blue-100 text-sm leading-relaxed max-w-md">
            One portal for your filings, drivers, trucks, FMCSA data and IFTA — built by truckers, for truckers.
          </p>

          <div className="mt-8 space-y-4 max-w-md">
            {VALUE_PROPS.map((vp, i) => {
              const Icon = vp.icon;
              return (
                <div key={i} className="flex items-start gap-3" data-testid={`login-value-${i}`}>
                  <div className="bg-blue-700/40 border border-blue-300/40 p-2 flex-shrink-0">
                    <Icon size={16} className="text-blue-100"/>
                  </div>
                  <div>
                    <div className="font-bold text-sm text-white">{vp.title}</div>
                    <div className="text-xs text-blue-100/90 leading-relaxed mt-0.5">{vp.text}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hs-overline text-blue-200 mt-8 pt-4 border-t border-blue-500/30">
            EST. 2025 · NORTH AMERICA · DOT-AWARE
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md hs-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center mb-8">
            <img src="/logo.png" alt="Crystal Logistics Services" className="h-20 w-auto"/>
          </div>
          <div className="hs-stripe" />
          <h2 className="font-display font-black text-3xl uppercase tracking-tight">Sign In</h2>
          <p className="text-sm text-slate-500 mt-2">Access your safety & compliance console.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="hs-overline">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="hs-input mt-2"
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="hs-overline">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="hs-input mt-2"
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <div className="text-right mt-2">
                <span className="text-xs hs-overline text-slate-400" data-testid="login-forgot-hint">
                  Forgot password? Contact your compliance provider.
                </span>
              </div>
            </div>
            {error && (
              <div className="text-sm text-red-600 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="hs-btn-primary w-full"
            >
              {loading ? "AUTHENTICATING…" : "SIGN IN"}
            </button>
          </div>

          <div className="hs-divider my-8" />
          <div className="text-sm text-slate-500">
            New to Crystal Logistics?{" "}
            <Link to="/register" className="text-blue-700 hover:underline font-semibold" data-testid="login-register-link">
              Create your portal
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
